/**
 * SafetyAckBand.tsx — was this patient told what the medicine can do to them?
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: medication safety
 * (2026-08-19).
 *
 * WHY: the clinician who raised this asked for exactly one thing before they
 * sign a prescription — "providing on the clinician portal we can see that the
 * patient has clearly read the risks of the medication and any relevant
 * conditions, we can then approve a prescription" (Reneal Patel, 2026-08-18).
 * The patient-side gate is only half a safety net: it stops an UNINFORMED sale,
 * but it tells the prescriber nothing. This is the half the prescriber reads.
 *
 * THREE STATES, AND THEY ARE NOT INTERCHANGEABLE. `staffOrder.safetyAck` is
 * nullable AND optional, and each value means something different to the person
 * about to approve:
 *
 *   an object   — recorded. Rendered as a FACT and nothing more: when, which
 *                 content version, how many topics, and who did the explaining.
 *                 It is deliberately not congratulatory and carries no tick-like
 *                 reassurance — it is a record the clinician weighs, not a
 *                 clearance the screen grants them.
 *   null        — THE CASE THIS COMPONENT EXISTS FOR. Nobody acknowledged the
 *                 risks. Shown loudly, because the alternative is a prescriber
 *                 approving in the belief that the checkout covered it. Never
 *                 hidden, and never rendered as an empty panel — an empty panel
 *                 is read as "nothing to worry about", which is the opposite of
 *                 what null means.
 *   undefined   — the orders revision behind this queue predates the field, so
 *                 THIS SCREEN DOES NOT KNOW. Collapsing it into `null` would
 *                 put "the risks were never acknowledged" on every order on an
 *                 older server — a false statement, and one that would train
 *                 clinicians to scroll past the band that matters. It gets its
 *                 own quieter wording, exactly as `KeyAlerts` separates a failed
 *                 check from a clear one, and it disappears the moment orders
 *                 ships the field.
 *
 * A11y — ONE ASSERTIVE REGION PER SURFACE. The R31 crisis banner owns the single
 * `role="alert"` on the review screen; two assertive regions interrupt each
 * other and a screen-reader user can lose one, and the safeguarding banner must
 * win. So every band here is a polite `role="status"`, matching KeyAlerts.tsx —
 * read that file's note before changing anything here. Prominence is carried by
 * tone, position and WORDS, never by colour alone (WCAG 2.1 AA 1.4.1).
 *
 * R28: this renders a version string, a count and an enum. No medication name,
 * no clinical prose, no topic wording — the topic keys are not printed either,
 * because a list of machine keys is noise to a clinician and the version is what
 * reconstructs the exact wording later.
 */
import type { StaffSafetyAcknowledgement } from '../api/types';
import { formatUtcDateTime } from '../lib/format';
import { Alert, WarningIcon } from '../ui/kit';

/** Who did the explaining, in words a clinician reads rather than a wire enum. */
const SOURCE_LABEL: Record<StaffSafetyAcknowledgement['source'], string> = {
  patient: 'The patient confirmed this themselves at checkout.',
  clinician_explained: 'A member of staff explained the risks and recorded it on the patient’s behalf.',
};

/**
 * The band. `ack` is the value straight off the order — pass it through
 * untouched, including `undefined`, because this component distinguishes the
 * three cases and a caller that normalises them first destroys the distinction.
 */
export function SafetyAckBand({ ack }: { ack: StaffSafetyAcknowledgement | null | undefined }) {
  if (ack === undefined) {
    return (
      <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-4)' }}>
        <strong style={{ display: 'block', marginBottom: 'var(--gps-s-1)' }}>
          Safety acknowledgement not recorded on this case
        </strong>
        <p style={{ margin: 0 }}>
          This order was created before we started recording what the patient was shown, so we cannot tell either way.
          Cover the medicine’s risks with them before you approve.
        </p>
      </Alert>
    );
  }

  if (ack === null) {
    return (
      <Alert tone="danger" role="status" style={{ marginBottom: 'var(--gps-s-4)' }}>
        <strong style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 'var(--gps-s-1)' }}>
          <WarningIcon size={15} />
          The patient has NOT acknowledged this medicine’s risks
        </strong>
        <p style={{ margin: 0 }}>
          Nobody has recorded that this patient was shown what this treatment can do to them, what to watch for, or
          when to stop and get urgent help.
        </p>
        <p style={{ margin: 'var(--gps-s-2) 0 0' }}>
          Cover the risks with them and record it before you approve. This does not block the decision — it is yours to
          make — but it is the conversation that has not happened yet.
        </p>
      </Alert>
    );
  }

  // The count, not the keys. A clinician needs to know the patient saw the full
  // set for this medicine; sixteen machine keys printed on a review screen is
  // noise that buries the one line above it that matters.
  const topicCount = ack.topicKeys.length;
  const when = formatUtcDateTime(ack.acknowledgedAt);

  return (
    <Alert tone="info" role="status" style={{ marginBottom: 'var(--gps-s-4)' }}>
      <strong style={{ display: 'block', marginBottom: 'var(--gps-s-1)' }}>
        Safety information acknowledged
      </strong>
      <p style={{ margin: 0 }}>
        {/* An unreadable timestamp drops the clause rather than printing
            "Invalid Date" on a clinical record — the same rule the key-alerts
            dates follow. The rest of the sentence still stands on its own. */}
        {when ? `Acknowledged ${when} (UTC). ` : ''}
        {topicCount} safety {topicCount === 1 ? 'topic was' : 'topics were'} shown, content version {ack.version}.
      </p>
      <p style={{ margin: 'var(--gps-s-2) 0 0' }}>{SOURCE_LABEL[ack.source] ?? SOURCE_LABEL.patient}</p>
    </Alert>
  );
}
